import React, { useState, useEffect, useRef } from "react";
import store from "../../../store";
import { Grid, Alert, Snackbar } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import "./_index.scss";

interface ClientNamesInterface {
  client_name: string;
  client_id: string;
}

const normalizeDateTime = (val: string) => {
  if (!val) return "";
  return val.slice(0, 16);
};

const CustomSelect: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({ value, options, onChange, placeholder = "Select...", disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find(o => o.value?.toString() === value?.toString());

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div
      className={`eu-custom-select ${isOpen ? "eu-custom-select--open" : ""} ${disabled ? "eu-custom-select--disabled" : ""}`}
      ref={ref}
    >
      <button
        type="button"
        className={`eu-input-box eu-input-box--select-btn ${isOpen ? "eu-input-box--focused" : ""}`}
        onClick={() => !disabled && setIsOpen(o => !o)}
        disabled={disabled}
      >
        <span className={selected ? "eu-select-val" : "eu-select-placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`eu-select-arrow ${isOpen ? "eu-select-arrow--open" : ""}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div className="eu-select-dropdown">
          {options.length === 0
            ? <div className="eu-select-empty">No options available</div>
            : options.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`eu-select-option ${value?.toString() === opt.value?.toString() ? "eu-select-option--active" : ""}`}
                  onClick={() => { onChange(opt.value); setIsOpen(false); }}
                >
                  <span className="eu-select-dot" />
                  <span>{opt.label}</span>
                  {value?.toString() === opt.value?.toString() && (
                    <svg className="eu-select-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              ))
          }
        </div>
      )}
    </div>
  );
};

const AddNewUserComponent = () => {
  const [sn, setSn]                           = useState(localStorage.getItem("sideNav"));
  const data                                  = useSelector((state: any) => state?.app);
  const windowWidth                           = useRef(window.innerWidth);
  const [open, setOpen]                       = useState(windowWidth.current > 1000 ? true : false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClientName, setSelectedClientName] = useState("");
  const [isSubmitted, setIsSubmitted]         = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showPassword, setShowPassword]       = useState(false);
  const [formData, setFormData]               = useState({
    name: "", password: "", user_type: "", client: "",
    operation: "", email_address: "", project_no: "",
    start_date: "", expected_date: "", client_id: "",
    forecast_id: "", mail_sent: false,
  });
  const [clientNames, setClientNames]         = useState<ClientNamesInterface[]>([]);
  const [operationsData, setOperationsData]   = useState<any[]>([]);
  const navigate                              = useNavigate();
  const [dateWarning, setDateWarning]         = useState("");
  const [submitError, setSubmitError]         = useState("");
  const [usernameError, setUsernameError]     = useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault();

  const validateDates = (startDate: any, expectedEndDate: any) => {
    if (startDate && expectedEndDate && new Date(expectedEndDate) < new Date(startDate)) {
      setDateWarning("Expected End Date cannot be earlier than Start Date.");
      return false;
    } else {
      setDateWarning("");
      return true;
    }
  };

  const getClientNames = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/getclients/`, {
        headers: { "Content-Type": "application/json" },
      });
      setClientNames(response.data);
    } catch (error) {
      console.error("Error fetching client names:", error);
    }
  };

  useEffect(() => { getClientNames(); }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === "expected_date" || name === "start_date") {
      const startDate   = name === "start_date"    ? value : formData.start_date;
      const expectedDate = name === "expected_date" ? value : formData.expected_date;
      validateDates(startDate, expectedDate);
    }
  };

  const getClientData = async (clientId: any) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/operations12/${clientId}/`);
      setOperationsData(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleClientChange = (v: string) => {
    const found = clientNames.find(o => o.client_id.toString() === v);
    if (!found) return;
    setSelectedClientId(found.client_id.toString());
    setSelectedClientName(found.client_name);
    getClientData(found.client_id);
    setFormData({
      name: "", password: "", user_type: "",
      client: found.client_name,
      operation: "", email_address: "",
      project_no: "", start_date: "", expected_date: "",
      client_id: found.client_id.toString(),
      forecast_id: "", mail_sent: false,
    });
  };

  const handleProjectChange = (v: string) => {
    const n = operationsData.find(o => o.forecast_id?.toString() === v);
    if (!n) return;
    setFormData(prev => ({
      ...prev,
      start_date:    normalizeDateTime(n?.start_date    || ""),
      expected_date: normalizeDateTime(n?.expected_date || ""),
      operation:     n?.forecast_description || "",
      project_no:    n?.project_no           || "",
      forecast_id:   n?.forecast_id          || "",
      client_id:     n?.client_id?.toString() || prev.client_id,
      client:        prev.client,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitted(true);
    setUsernameError(false);
    if (formData.start_date && formData.expected_date && new Date(formData.expected_date) < new Date(formData.start_date)) {
      setSubmitError("Expected End Date cannot be earlier than Start Date.");
      return;
    } else { setSubmitError(""); }

    const newData = {
      name:           formData.name,
      password:       formData.password.toString(),
      user_type:      formData.user_type.toString(),
      client:         formData.client.toString(),
      operation:      formData.operation.toString(),
      email_address:  formData.email_address.toString(),
      project_no:     formData.project_no    || "",
      start_date:     formData.start_date    || "",
      expected_date:  formData.expected_date || "",
      client_id:      formData.client_id.toString(),
      forecast_id:    formData.forecast_id.toString(),
      mail_sent:      formData.mail_sent,
    };

    fetch(`${process.env.REACT_APP_BACKEND_IP}/api/user/save/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newData),
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === "username already exists") {
          setUsernameError(true);
          setSubmitError("Username already exists. Please choose a different username.");
        } else if (data.status === "invalid data") {
          setSubmitError("Invalid data. Please check all fields.");
        } else {
          setShowSuccessAlert(true);
          setTimeout(() => navigate("/usermanagement"), 1500);
        }
      })
      .catch(error => {
        console.error("Error:", error);
        setSubmitError("Failed to save user. Please try again.");
      });
  };

  useEffect(() => { setSn(localStorage.getItem("sideNav")); }, []);
  useEffect(() => { store.dispatch({ type: "TOGGLE_MENU", payload: windowWidth.current > 1000 ? true : false }); }, []);
  useEffect(() => { setOpen(data.toggle); }, [data]);

  return (
    <div style={open ? { marginLeft: 260, width: "calc(100% - 260px)" } : {}} className="mainas">
      <div className="eu-wrap">

        <div className="eu-header">
          <div className="eu-header-left">
            <button className="eu-back-btn" onClick={() => navigate("/usermanagement")}>
              <ArrowBackIcon />
            </button>
            <div>
              <h1 className="eu-heading">Add New User</h1>
              <p className="eu-subheading">Create a new user and assign access</p>
            </div>
          </div>
          <button className="eu-save-btn" type="button" onClick={handleSubmit as any}>
            <SaveIcon style={{ fontSize: 16 }} />
            Create User
          </button>
        </div>

        <div className="eu-card">
          <form onSubmit={handleSubmit} autoComplete="off">
            <input type="text"     name="prevent_autofill"    style={{ display: "none" }} autoComplete="off"          readOnly />
            <input type="password" name="prevent_autofill_pw" style={{ display: "none" }} autoComplete="new-password" readOnly />

            <Grid container spacing={3}>

              <Grid item xs={12} md={6}>
                <label className="eu-label">Client *</label>
                <CustomSelect
                  value={selectedClientId}
                  options={clientNames.map(o => ({ value: o.client_id.toString(), label: o.client_name }))}
                  onChange={handleClientChange}
                  placeholder="Select Client"
                />
              </Grid>

              {selectedClientName && (
                <Grid item xs={12} md={6}>
                  <label className="eu-label">Project No *</label>
                  <CustomSelect
                    value={formData.forecast_id}
                    options={operationsData.map(o => ({ value: o.forecast_id?.toString(), label: o.forecast_description }))}
                    onChange={handleProjectChange}
                    placeholder="Select Project"
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <label className="eu-label">Username *</label>
                <div className={`eu-input-box ${usernameError ? "eu-input-box--error" : ""}`}>
                  <input className="eu-input" name="name" required autoComplete="off"
                    value={formData.name} onChange={handleInputChange}
                    placeholder="Enter username" />
                </div>
                {usernameError && <p className="eu-field-error">Username already exists</p>}
              </Grid>

              <Grid item xs={12} md={6}>
                <label className="eu-label">Password *</label>
                <div className="eu-input-box eu-input-box--addon">
                  <input className="eu-input" name="password" required autoComplete="new-password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password} onChange={handleInputChange}
                    placeholder="Enter password" />
                  <button type="button" className="eu-pass-toggle"
                    onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} tabIndex={-1}>
                    {showPassword ? <VisibilityOff style={{ fontSize: 18 }} /> : <Visibility style={{ fontSize: 18 }} />}
                  </button>
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <label className="eu-label">User Type *</label>
                <CustomSelect
                  value={formData.user_type}
                  options={[{ value: "user", label: "User" }, { value: "admin", label: "Admin" }]}
                  onChange={v => setFormData(prev => ({ ...prev, user_type: v }))}
                  placeholder="Select User Type"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <label className="eu-label">Email Address</label>
                <div className="eu-input-box">
                  <input className="eu-input" name="email_address" type="email"
                    value={formData.email_address} onChange={handleInputChange}
                    placeholder="Enter email address" />
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <label className="eu-label">Start Date</label>
                <div className="eu-input-box">
                  <input className="eu-input" name="start_date" type="datetime-local"
                    value={formData.start_date} onChange={handleInputChange} />
                </div>
              </Grid>

              <Grid item xs={12} md={6}>
                <label className="eu-label">Expected End Date</label>
                <div className="eu-input-box">
                  <input className="eu-input" name="expected_date" type="datetime-local"
                    value={formData.expected_date} onChange={handleInputChange} />
                </div>
              </Grid>

            </Grid>

            {dateWarning && <Alert severity="warning" sx={{ mt: 3, borderRadius: "12px" }}>{dateWarning}</Alert>}
            {submitError  && <Alert severity="error"   sx={{ mt: 3, borderRadius: "12px" }}>{submitError}</Alert>}
          </form>
        </div>

      </div>

      <Snackbar open={showSuccessAlert} autoHideDuration={1500} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="success" sx={{ borderRadius: "12px" }}>User created successfully!</Alert>
      </Snackbar>
    </div>
  );
};

export default AddNewUserComponent;